import Link from 'next/link';
import { useExtendedSearchParams } from '@hooks/next-routing-wrappers';
import { useTranslation } from 'next-i18next';
import {
  Anchor, Avatar, Button, BadgePill, Card, ErrorBoundary, RatingScore,
  TailwindSpinner,
} from '@components/common';

import {
  BoltIcon, ArrowRightIcon, CheckIcon, ArrowPathIcon,
} from '@heroicons/react/20/solid';
import {
  EyeIcon, MapPinIcon, CurrencyEuroIcon, BookmarkIcon,
} from '@heroicons/react/24/outline';
import { convertToLocalizedDate } from '@utils/date-time/date-time';
import { CardJobTalentActions } from '@components/templates/job/Card/CardJobTalentActions';
import {
  Dispatch, forwardRef, memo, useContext, useMemo, useState, SetStateAction, MouseEvent, useEffect, useRef, useCallback,
} from 'react';
import { Input, RecordInterface } from '@components/common/Forms';
import { CardAnimations } from '@type/card';
import { JobTrackingContext } from '@components/contexts/JobTrackingContext';
import { getI18nLinkHref } from '@services/i18nPaths';
import { clsx } from 'clsx';
import { buildJobDetailUrlParams } from '@utils/job/buildJobDetailUrlParams';
import { BaseTrackingEventData, trackEvent } from '@services/tracking';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { useJobTalentActions } from '@root/src/hooks/features/useJobTalentActions';
import { JobPlacement } from '@root/src/types/job-params';
import { useInView } from 'react-intersection-observer';

const googleApiKey = process.env.GOOGLE_MAPS_CLIENT_API_KEY as string;

// Interface
// ******************************************
export interface CardJobTalentBulkApplyFormProps {
  record: RecordInterface,
  name: string,
  setSelectAll?: Dispatch<SetStateAction<boolean | undefined>>,
}

export interface CardJobTalentProps {
  job: ApiSchema<'JobItem'>,
  locale: Locale,
  animation?: CardAnimations,
  className?: string,
  openInModal?: boolean,
  bulkApply?: CardJobTalentBulkApplyFormProps,
  trackingData? : BaseTrackingEventData;
  position?: number,
  beforeModalOpen?: () => void;
  isTopJob?: boolean,
  isApplication?: boolean,
  forceModalOnMc?: boolean,
  useCurrentUrlForModal?: boolean,
  withAuthModal?: boolean,
  allowAfterApply?: boolean,
  onClick?: (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
}

/**
 * @description CardJobTalent component.
 *
 * - job: *ApiSchema<'JobItem'>* - The job item. Required.
 * - locale - *Locale* - The current app locale.
 * - animation: *CardAnimations* - The animation for the card. Optional. Default is 'riseUp'.
 * - className: *string* - Additional classes for the card. Optional.
 * - openInModal: *boolean* - If true, the job will open in a modal. Optional. Default is true.
 * - bulkApply: *object* - The object containing the record function and the job slug for bulk apply. Optional. Default is undefined.
 * - position: *number* - The index position of the job card in the list. Used for tracking. Optional.
 * - beforeModalOpen: *function* - Callback function that gets executed before modal open.
 * - isTopJob: *boolean* - If true, the job is a top job. Optional.
 * - trackingData: *string* - The object for getting tracking details from original list
 * - isApplication: *boolean* - If true, the job is an application. Optional.
 * - forceModalOnMc: *boolean* - If true, MC cards use the modal URL flow instead of routing to the MC detail page.
 * - useCurrentUrlForModal: *boolean* - If true, modal opening keeps the current page path and only adds selectedJobSlug.
 * - withAuthModal: *boolean* - If true, unauthenticated apply opens the auth modal instead of redirecting/easy apply.
 * - allowAfterApply: *boolean* - If false, successful apply stays on the current page.
 *
 * BulkApply object:
 * - record: *RecordInterface* - The record function from useBjForm. Required.
 * - name: *string* - The name of the form group. Required.
 * - setSelectAll: *Dispatch<SetStateAction<boolean | undefined>>* - The function to set the 'select all' checkbox. Optional. Default is undefined.
 */
export const CardJobTalent = forwardRef<HTMLDivElement, CardJobTalentProps>((props, ref) => {
  // Props
  const {
    job,
    locale,
    animation = 'riseUp',
    className = '',
    position,
    openInModal = false,
    bulkApply,
    beforeModalOpen,
    trackingData,
    isApplication = false,
    useCurrentUrlForModal = false,
    withAuthModal = false,
    allowAfterApply = true,
    onClick,
  } = props;

  const {
    record,
    name,
    setSelectAll,
  } = bulkApply || {};

  // Translation
  const { t } = useTranslation('common');

  const { searchParams } = useExtendedSearchParams();

  const headerInfoData = useAppSelector(getUserHeaderInfo);
  const userId = headerInfoData?.userId;

  // On the applications list, evidence jobs that still have interview questions waiting to be
  // answered with a distinct border.
  const hasWaitingInterview = isApplication && (job.unansweredJobInterviewQuestions?.length ?? 0) > 0;

  // Common classes
  // *****************************************
  const cardClassesBase = {
    common: 'relative rounded-md bg-surface border border-y-3 overflow-hidden group',
    highlighted: hasWaitingInterview
      ? 'border-primary'
      : (job.highlighted ? 'border-secondary/50' : 'border-transparent'),
    checked: 'has-[:checked]:ring-[3px] has-[:checked]:ring-secondary/20 has-[:checked]:after:block has-[:checked]:after:rounded-md has-[:checked]:after:border has-[:checked]:after:border-secondary has-[:checked]:after:absolute has-[:checked]:after:-inset-x-px has-[:checked]:after:-inset-y-1',
    custom: className || '',
  };
  const cardClasses = Object.values(cardClassesBase).join(' ');
  const applicantBadgeClasses = 'font-medium !bg-transparent !px-0';
  const appliedAtClasses = 'flex gap-x-2 text-xs leading-5 !px-0';

  // Helper variables
  // *****************************************
  const jobSlug = job.slug;
  const jobLocation = job.locations && job.locations.length > 0 ? job.locations[0] : null;

  // Truncate location name to 40 characters (including ellipsis)
  const truncateLocationName = (locationName: string | undefined, maxLength: number = 40): string => {
    if (!locationName) return '';
    if (locationName.length <= maxLength) return locationName;
    return `${locationName.substring(0, maxLength - 3)}...`;
  };

  const displayLocationName = jobLocation?.name ? truncateLocationName(jobLocation.name) : '';

  const handleOpenMapModal = () => {
    if (!googleApiKey || !locale || !jobLocation?.geoLocation) return;
    const { lat, lng } = jobLocation.geoLocation;
    if (typeof lat !== 'number' || typeof lng !== 'number') return;
    void (async () => {
      const { showJobsMapModal } = await import('@components/templates/job/list/JobsMapModal');
      showJobsMapModal({
        googleApiKey,
        locale,
        initialCenter: { lat, lng },
        initialZoom: 15,
        jobSlug: job.slug,
      });
    })();
  };
  const allowFastApply = job?.state !== 'archived' && (job?.state !== undefined || job?.active !== undefined || !!job?.appliedAt);


  // Conditions for showing badges
  // *****************************************
  const showBadge = {
    responsive: !job.premium && job.responsive,
    firstApplicant: job.active && job.applications === 0,
    amongFirstApplicants: job.active && !job.appliedAt && typeof job.applications === 'number' && job.applications > 0 && job.applications < 6,
    autoApply: job.autoApply,
  };

  // Conditions for locations
  // *****************************************
  const showLocation = {
    first: job.locations && job.locations.length > 0,
    more: job.locations && job.locations.length > 1,
  };

  // Conditions for salary label
  // *****************************************
  const salaryLabel = {
    default: job.salary,
    estimate: !job.salary && job.estimatedSalary,
  };

  // Get tracking context data
  // **************************************
  const {
    requestId,
    listType,
    searchId,
  } = useContext(JobTrackingContext) || {};

  const {
    isFavorite,
    isLoadingFavoritesAction,
    removeFromFavorites,
    addToFavorites,
  } = useJobTalentActions({
    jobSlug: job.slug ?? '',
    jobId: job.id,
    jobTitle: job.title,
    company: job.companyName,
    likedProp: job.liked,
    dislikedProp: job.disliked,
    appliedProp: !!job.appliedAt,
    requestId,
    placement: JobPlacement.CARD,
  });

  // Helpers for job url.
  // ************************************
  // Default job url — the localized job detail URL.
  const defaultJobUrl = useMemo(
    () => getI18nLinkHref(`/job/${jobSlug}`, locale),
    [jobSlug, locale],
  );

  // State for job url. It will be updated with query params.
  // **************************************
  const [jobUrl, setJobUrl] = useState<string>(defaultJobUrl);

  // Build search url params to be attached to the job url
  // **************************************
  const urlSearchParams = buildJobDetailUrlParams({
    requestId,
    position,
    searchId,
  });

  // Update job url. Append query params to the default job url.
  // *****************************************
  const updateJobUrl = () => {
    setJobUrl(`${defaultJobUrl}${urlSearchParams.toString() ? `?${urlSearchParams.toString()}` : ''}`);

    if (openInModal && beforeModalOpen) {
      beforeModalOpen();
    }
  };

  const jobTitle = job.title;

  // Open job detail modal
  // *****************************************
  const handleJobOpening = (e: MouseEvent<HTMLAnchorElement>) => {
    // Check if Ctrl (Windows/Linux) or Meta (Mac) key is pressed
    trackEvent({
      eventType: 'click',
      entityType: 'job',
      entityId: jobSlug ?? '',
      viewContext: 'card',
      ...(trackingData?.listUuid && { listUuid: trackingData.listUuid }),
      ...(trackingData?.listType && { listType: trackingData.listType }),
      ...(trackingData?.position && { position: trackingData.position }),
      ...(userId && { userId: userId.toString() }),
    });

    if (onClick) {
      onClick(e);
      if (e.defaultPrevented) return;
    }

    if (e.ctrlKey || e.metaKey) {
      // Allow the default behavior to open in a new tab
      return;
    }

    // Prevent default action for regular clicks
    e.preventDefault();

    // Build the params object from existing searchParams
    const params = new URLSearchParams(searchParams?.toString());

    // Set the selectedJobSlug parameter
    params.set('selectedJobSlug', jobSlug as string);

    // Construct the new URL with proper query parameters
    const modalBaseUrl = useCurrentUrlForModal && typeof window !== 'undefined'
      ? window.location.pathname
      : jobUrl;
    const newUrl = `${modalBaseUrl}${modalBaseUrl.includes('?') ? '&' : '?'}${params.toString()}`;

    window.history.pushState(null, '', newUrl);
  };

  const hasTrackedView = useRef<boolean>(false);
  const jobSlugRef = useRef(jobSlug);
  const userIdRef = useRef(userId);
  const trackingDataRef = useRef(trackingData);

  const { ref: inViewRef, inView } = useInView({ threshold: 1, triggerOnce: true });

  // Merge forwarded ref with inViewRef
  const mergedRef = useCallback((node: HTMLDivElement | null) => {
    inViewRef(node);
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref, inViewRef]);

  // Added tracking seen event
  useEffect(() => {
    if (trackingDataRef.current?.disableTracking) return;
    if (inView && !hasTrackedView.current) {
      trackEvent({
        eventType: 'view',
        entityType: 'job',
        entityId: jobSlugRef.current ?? '',
        viewContext: 'card',
        ...(userIdRef.current && { userId: userIdRef.current.toString() }),
        ...(trackingDataRef.current?.listUuid && { listUuid: trackingDataRef.current.listUuid }),
        ...(trackingDataRef.current?.listType && { listType: trackingDataRef.current.listType }),
        ...(trackingDataRef.current?.position && { position: trackingDataRef.current.position }),
      });

      hasTrackedView.current = true;
    }
  }, [inView]);

  // Render component
  // *****************************************
  return (
    <Card
      ref={mergedRef}
      animation={animation}
      className={cardClasses}
    >
      <ErrorBoundary fallback={<div className="flex items-center justify-center p-3 text-error">Error loading job data.</div>}>

        {/*
          Full-card link to the job details page.
          Uses an anchor tag for SEO when opened in a modal.
          Defaults to the Next.js Link component otherwise.
        */}
        {openInModal ? (
          <a
            href={jobUrl}
            onMouseDown={updateJobUrl}
            onClick={handleJobOpening}
            className="absolute inset-0 z-1"
            aria-label={job.title}
          >
            <span className="hidden">{job.title}</span>
          </a>
        ) : (
          <Link
            href={jobUrl}
            onMouseDown={updateJobUrl}
            className="absolute inset-0 z-1"
            aria-label={job.title}
            onClick={(e) => {
              if (onClick) {
                onClick(e);
                if (e.defaultPrevented) return;
              }
              trackEvent({
                eventType: 'click',
                entityType: 'job',
                entityId: jobSlug ?? '',
                viewContext: 'card',
                ...(trackingData?.listUuid && { listUuid: trackingData.listUuid }),
                ...(trackingData?.listType && { listType: trackingData.listType }),
                ...(trackingData?.position && { position: trackingData.position }),
                ...(userId && { userId: userId.toString() }),
                metadata: [
                  { key: 'inModal', value: 'true' },
                ],
              });
            }}
          >
            <span className="hidden">{job.title}</span>
          </Link>
        )}

        {/* Bulk Apply checkbox; uncheck 'select all' checkbox on click */
          record && name && (
            <label
              htmlFor={`${name}_${jobSlug}`}
              aria-label={t('global.default.select.option')}
              title={t('global.default.select.option')}
              className="absolute right-0 top-0 z-2 cursor-copy p-1"
            >
              <Input
                defaultValue={jobSlug}
                type="checkbox"
                className="flex size-6 cursor-copy text-secondary ring-[3px] ring-surface checked:bg-secondary"
                {...record(name, {
                  id: `${name}_${jobSlug}`,
                  valueAsArray: true,
                })}
                onClick={() => {
                  if (setSelectAll) setSelectAll(undefined);
                }}
              />
            </label>
          )
        }

        {/* CARD BODY */}
        <Card.Body className="relative grid grid-cols-10 !pb-0 !pl-3 !pr-5 !pt-5">
          <div className="col-span-7">
            {/* Top actions */}
            {showBadge.autoApply && (
              <div className="-mt-2 mb-4 flex flex-col items-start gap-2">

                {/* Auto-apply badge */}
                { showBadge.autoApply && (
                  <BadgePill styling="soft" size="pico" color="pink" className="font-semibold uppercase">
                    <ArrowPathIcon className="size-4 fill-current" />
                    {t('job.card.autoApply')}
                  </BadgePill>
                )}
              </div>
            )}

            {/* Job title - max 2 lines */}
            <h2 className="line-clamp-2 text-base font-bold leading-6">{jobTitle}</h2>

            {/* Badges 'be first / among first' applicants OR 'Applied at' info - put them outside Card.Body to be properly aligned */}
            <div className="mt-2">
              {/* Badge first applicant */}
              { showBadge.firstApplicant && (
                <BadgePill color="secondary" styling="soft" className={applicantBadgeClasses}>
                  <BoltIcon className="size-3 fill-current" />
                  {t('first.applicant.badge')}
                </BadgePill>
              )}
              {/* Badge among first applicants */}
              { showBadge.amongFirstApplicants && (
                <BadgePill color="primary-light" styling="soft" className={applicantBadgeClasses}>
                  <ArrowRightIcon className="size-3 fill-current" />
                  {t('less.applicants.badge')}
                </BadgePill>
              )}
              {/* Applied at */}
              {isApplication && job.appliedAt && (
                <>
                  <div className={clsx(appliedAtClasses, 'text-ink-medium')}>
                    <CheckIcon className="size-4" />
                    {`${t('global.label.applied_at')} ${convertToLocalizedDate(job.appliedAt, locale)}`}
                  </div>
                  <div className={clsx(appliedAtClasses, job.applicationViewedAt ? 'text-secondary-light' : 'text-ink-medium')}>
                    <EyeIcon className="size-4" />
                    {
                      job.applicationViewedAt
                        ? `${t('job.card.viewed_at')} ${convertToLocalizedDate(job.applicationViewedAt, locale)}`
                        : t('job.card.unviewed')
                    }
                  </div>
                </>
              )}
            </div>

            {/* Employer name */}
            <div className="mt-2 line-clamp-1 w-full text-sm text-ink-medium">
              {job.companyName}
            </div>
          </div>
          <div className="col-span-3">
            {/* Favorites / Like button */}
            {!bulkApply && (
              <Button
                type="button"
                rounding="full"
                styling="none"
                size="sm"
                className="group/bookmarkButton absolute right-0 top-0 z-2 mr-1 mt-1"
                onClick={isFavorite ? removeFromFavorites : addToFavorites}
              >
                {isLoadingFavoritesAction ? (
                  <TailwindSpinner className="size-5" />
                ) : (
                  <BookmarkIcon className={clsx(isFavorite && 'fill-current', 'size-5 text-warning group-hover/bookmarkButton:fill-current')} />
                )}
              </Button>
            )}

            {/* Employer logo */}
            <div className="relative flex h-15 items-center justify-center">
              <Avatar
                imgPath={job.companyLogo}
                imgFolder="employer_logo"
                extractImageName
                name={job.companyName}
                size="md"
                aspectRatio="square-fit"
                rounding="none"
                id={(job.companyName || job.slug || '').charCodeAt(0)}
              />
            </div>
            {/* Employer rating */}
            { (job.rating !== undefined && job?.rating > 0) && (
              <div className="mt-2 flex justify-center sm:h-5">
                <RatingScore score={job.rating} showScore={false} className="size-4 text-warning" />
              </div>
            )}
            {/* Badges premium / responsive */}
            <div className="relative mt-2 text-center sm:h-7">
              {/* Badge employer responsive */}
              { showBadge.responsive && (
                <BadgePill size="pico" color="primary" styling="soft" className="truncate">
                  {t('employer.card.responsive')}
                </BadgePill>
              )}
            </div>
          </div>
        </Card.Body>


        {/* CARD FOOTER */}
        <Card.Footer
          className={clsx('relative z-2 mt-3 bg-surface px-3', { 'can-hover:transition-all can-hover:duration-200 can-hover:ease-in-out can-hover:h-13 can-hover:group-hover:-translate-y-13': allowFastApply })}
        >
          <div className="grid h-13">
            {/* Job locations; we only display the first location */}
            <div className="text-left text-sm text-ink-medium">
              {(job.salary || job.estimatedSalary) && (
              <div className="flex items-center tracking-wider">
                <CurrencyEuroIcon className="mr-1 h-4" />
                {`${job.salary || job.estimatedSalary}`}
                { salaryLabel.estimate && ` (${t('salary.estimate')})` }
              </div>
              )}

              <div className="flex items-center tracking-wider">
                {showLocation.first && <MapPinIcon className="mr-1 h-4" />}
                { // show first location
                  showLocation.first && jobLocation && (
                    jobLocation.withFullAddress ? (
                      <Button
                        type="button"
                        styling="none"
                        className="relative z-2 hover:underline"
                        onClick={handleOpenMapModal}
                        title={jobLocation.name}
                      >
                        {displayLocationName}
                      </Button>
                    ) : (
                      <Anchor
                        symfonyRoute
                        href="app_job_city"
                        symfonyParams={{ location: jobLocation.slug }}
                        styling="none"
                        className="relative z-2 hover:underline"
                        title={jobLocation.name}
                      >
                        {displayLocationName}
                      </Anchor>
                    )
                  )
                }
                { showLocation.more && <span className="inline-block">, ...</span> }
              </div>
            </div>
          </div>

          {/* Job actions */}
          {allowFastApply && (
            <CardJobTalentActions
              job={job}
              requestId={requestId}
              position={position}
              listType={listType}
              withAuthModal={withAuthModal}
              allowAfterApply={allowAfterApply}
            />
          )}
        </Card.Footer>

      </ErrorBoundary>
    </Card>
  );
});

CardJobTalent.displayName = 'CardJobTalent';

/**
 * @description Memoized CardJobTalent component. To be used in the infinite lists.
 */
export const CardJobTalentMemo = memo(CardJobTalent);
